import React, { memo, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Box, Button, Chip, Typography } from '@mui/material';
import PageBlock from '@sharedCR/common/PageBlock';
import { TextFieldCDS } from '@sharedCR/coreDesignSystem/form';
import { DateIcon } from '@sharedCR/coreDesignSystem/icons';
import PropTypes from 'prop-types';

import { TaskStatus } from 'constants/tasks';

import { approveScheduleClosingTaskAction, rejectTaskAction } from 'store/loanApplications/actions';

import FieldRow from '../FieldRow';

import { DEFAULT_CHIP_COLOR, StatusChipColorMap } from './ScheduleClosingSection.constants';
import {
  headerRowStyles,
  locationWrapperStyles,
  rejectActionsStyles,
  rejectWrapperStyles,
  sectionWrapperStyles,
  slotCardStyles,
  slotInfoStyles,
  slotsWrapperStyles,
  statusChipStyles,
} from './ScheduleClosingSection.styles';
import { ScheduleClosingSectionTestIds } from './ScheduleClosingSection.testIds';
import {
  formatSlotDateTime,
  getFormattedAddress,
  getScheduleClosingLocationLabel,
  getScheduleClosingStatusLabel,
  parseScheduleClosingMetadata,
} from './ScheduleClosingSection.utils';

const ScheduleClosingSection = ({ task }) => {
  const dispatch = useDispatch();

  const [rejectionMessage, setRejectionMessage] = useState('');
  const [isRejecting, setIsRejecting] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const metadata = parseScheduleClosingMetadata(task?.metadata);
  const slots = metadata?.proposedSlots || [];
  const preferredLocation = metadata?.location;
  const formattedAddress = getFormattedAddress(metadata);
  const taskStatus = task?.status;
  const isPending = taskStatus === TaskStatus.SUBMITTED;

  const handleApprove = useCallback(
    e => {
      const { slotIndex } = e.currentTarget.dataset;

      setIsProcessing(true);
      dispatch(
        approveScheduleClosingTaskAction({
          taskId: task.taskId,
          selectedSlotIndex: Number(slotIndex),
        }),
      ).finally(() => setIsProcessing(false));
    },
    [dispatch, task?.taskId],
  );

  const handleRejectClick = useCallback(() => {
    setIsRejecting(true);
  }, []);

  const handleCancelReject = useCallback(() => {
    setIsRejecting(false);
    setRejectionMessage('');
  }, []);

  const handleRejectConfirm = useCallback(() => {
    setIsProcessing(true);
    dispatch(
      rejectTaskAction({
        taskId: task.taskId,
        userNote: rejectionMessage,
      }),
    ).finally(() => {
      setIsProcessing(false);
      setIsRejecting(false);
      setRejectionMessage('');
    });
  }, [dispatch, task?.taskId, rejectionMessage]);

  const handleRejectionMessageChange = useCallback(e => {
    setRejectionMessage(e.target.value);
  }, []);

  if (!task) {
    return null;
  }

  return (
    <PageBlock title="Schedule Closing">
      <Box sx={sectionWrapperStyles}>
        <Box sx={headerRowStyles}>
          <Typography variant="medium16">Borrower Request</Typography>
          <Chip
            color={StatusChipColorMap[taskStatus] || DEFAULT_CHIP_COLOR}
            label={getScheduleClosingStatusLabel(taskStatus)}
            size="small"
            sx={statusChipStyles}
          />
        </Box>

        <Box sx={locationWrapperStyles}>
          <FieldRow
            label="Preferred Location"
            value={getScheduleClosingLocationLabel(preferredLocation)}
          />
          {formattedAddress !== '-' && <FieldRow label="Address" value={formattedAddress} />}
        </Box>

        <Box>
          <Typography color="text.secondary" variant="regular14">
            Proposed Date/Time Slots
          </Typography>
          <Box sx={slotsWrapperStyles}>
            {slots.map((slot, idx) => (
              <Box key={`slot-${slot.date}-${slot.time}`} sx={slotCardStyles}>
                <Box sx={slotInfoStyles}>
                  <DateIcon />
                  <Typography variant="medium14">{formatSlotDateTime(slot)}</Typography>
                </Box>
                {isPending && (
                  <Button
                    color="primary"
                    data-slot-index={idx}
                    data-testid={ScheduleClosingSectionTestIds.getApproveButtonId(idx)}
                    disabled={isProcessing}
                    size="small"
                    variant="contained"
                    onClick={handleApprove}
                  >
                    Approve
                  </Button>
                )}
              </Box>
            ))}
          </Box>
        </Box>

        {isPending && !isRejecting && (
          <Box sx={rejectActionsStyles}>
            <Button
              color="error"
              data-testid={ScheduleClosingSectionTestIds.REJECT_ALL_BUTTON}
              disabled={isProcessing}
              size="small"
              variant="outlined"
              onClick={handleRejectClick}
            >
              Reject All
            </Button>
          </Box>
        )}

        {isRejecting && (
          <Box sx={rejectWrapperStyles}>
            <TextFieldCDS
              multiline
              dataTestId={ScheduleClosingSectionTestIds.REJECTION_MESSAGE_INPUT}
              label="Rejection Message"
              name="rejectionMessage"
              placeholder="Provide a reason for rejecting the proposed slots..."
              rows={3}
              value={rejectionMessage}
              onChange={handleRejectionMessageChange}
            />
            <Box sx={rejectActionsStyles}>
              <Button
                color="inherit"
                data-testid={ScheduleClosingSectionTestIds.CANCEL_REJECT_BUTTON}
                disabled={isProcessing}
                size="small"
                variant="outlined"
                onClick={handleCancelReject}
              >
                Cancel
              </Button>
              <Button
                color="error"
                data-testid={ScheduleClosingSectionTestIds.CONFIRM_REJECT_BUTTON}
                disabled={isProcessing || !rejectionMessage.trim()}
                size="small"
                variant="contained"
                onClick={handleRejectConfirm}
              >
                Confirm Reject
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </PageBlock>
  );
};

ScheduleClosingSection.propTypes = {
  task: PropTypes.shape({
    taskId: PropTypes.string,
    status: PropTypes.string,
    metadata: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        proposedSlots: PropTypes.arrayOf(
          PropTypes.shape({
            date: PropTypes.string,
            time: PropTypes.string,
          }),
        ),
        location: PropTypes.string,
        locationDetails: PropTypes.shape({
          address: PropTypes.string,
          description: PropTypes.string,
        }),
      }),
    ]),
  }),
};

export default memo(ScheduleClosingSection);
